import utils from "../../../utils/utils";
import { gateNameForCode, STUDY_GATE_OPTIONS } from "../isf/isfStudyGateService";

export const STUDY_MGMT_TRUNCATE_LEN = 15;

export const STUDY_MGMT_AVATAR_PALETTE = [
  "#1d4ed8",
  "#6d28d9",
  "#047857",
  "#b45309",
  "#b91c1c",
  "#0e7490",
  "#4338ca",
  "#0f766e",
  "#7c2d12",
  "#be185d",
  "#334155",
  "#4c1d95",
];

export const getStudyMgmtAvatarColor = (str) => {
  if (!str || typeof str !== "string") return STUDY_MGMT_AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % STUDY_MGMT_AVATAR_PALETTE.length;
  return STUDY_MGMT_AVATAR_PALETTE[idx];
};

export const SCHEDULE_STATUS_PUBLISHED = "Published";
export const SCHEDULE_STATUS_DRAFT = "Draft";
export const SCHEDULE_STATUS_NONE = "No Schedule";

export const STUDY_MGMT_EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

export { DEFAULT_PRESETS as STUDY_MGMT_COLUMN_DATE_PRESETS } from "../../common/DateRangeFilter";

export const STUDY_MGMT_FILTERS_INITIAL = {
  groupTypes: [],
  piNames: [],
  createdDateRange: { ...STUDY_MGMT_EMPTY_DATE_RANGE },
  status: "",
  gateCodes: [],
};

const GROUP_TYPE_SINGLE = "Single Group";
const GROUP_TYPE_MULTIPLE = "Multiple Group";
const GROUP_TYPE_NA = "N/A";

export const isGroupTypeUnavailable = (row) => {
  const type = String(row?.GroupType ?? "").trim();
  return !type || type.toUpperCase() === GROUP_TYPE_NA;
};

export const normalizeGroupTypeFilterValue = (row) => {
  if (isGroupTypeUnavailable(row)) return GROUP_TYPE_NA;
  return String(row.GroupType).trim();
};

export const isSingleGroupType = (row) =>
  normalizeGroupTypeFilterValue(row) === GROUP_TYPE_SINGLE;

export const isMultipleGroupType = (row) =>
  normalizeGroupTypeFilterValue(row) === GROUP_TYPE_MULTIPLE;

export const getStudyRowId = (row) =>
  String(row?.StudyId ?? row?.StudyIdRef ?? "").trim();

export const studyHasScheduleSetup = (row) => {
  const status = String(row?.StudySetupStatus ?? "").trim();
  return status === "1";
};

/** Real ESch_StudySchedule id — only when setup exists (API aliases TrialStudy.StudyId when none). */
export const getStudyScheduleId = (row) => {
  if (!studyHasScheduleSetup(row)) return "";
  return String(row?.StudyScheduleId ?? row?.studyScheduleId ?? "").trim();
};

export const getStudySchedulePublishLabel = (row, scheduleStatusByStudyId = {}) => {
  const studyId = getStudyRowId(row);
  const enriched = scheduleStatusByStudyId[studyId];
  if (enriched) return enriched;

  return resolveStudyScheduleStatusLabel(row);
};

export const isStudyScheduleLiveFromRow = (row = {}) => {
  const liveFlag =
    row?.IsCohortScheduleLive ??
    row?.isCohortScheduleLive ??
    row?.LiveStatus ??
    row?.liveStatus;
  return liveFlag === true || liveFlag === 1 || liveFlag === "1";
};

/** Authoritative label when cohort API succeeded; falls back to list-row flags when it did not. */
export const resolveStudyScheduleStatusLabel = (row, cohortResult = null) => {
  if (isGroupTypeUnavailable(row) || !studyHasScheduleSetup(row)) {
    return SCHEDULE_STATUS_NONE;
  }
  if (cohortResult && typeof cohortResult.isScheduleLive === "boolean") {
    return cohortResult.isScheduleLive
      ? SCHEDULE_STATUS_PUBLISHED
      : SCHEDULE_STATUS_DRAFT;
  }
  if (isStudyScheduleLiveFromRow(row)) return SCHEDULE_STATUS_PUBLISHED;
  return SCHEDULE_STATUS_DRAFT;
};

export const getStudyStatusLabel = (row) => {
  const status = String(row?.StudyStatus ?? "").trim().toLowerCase();
  if (
    status === "archived" ||
    status === "archive" ||
    row?.InActive === 1 ||
    row?.InActive === true ||
    row?.Archive === 1 ||
    row?.Archive === true
  ) {
    return "Archived";
  }
  return "Live";
};

export const getStudyPiName = (row) => String(row?.PIName ?? "").trim() || "—";

export const truncateStudyText = (value, maxLen = STUDY_MGMT_TRUNCATE_LEN) => {
  const text = String(value ?? "").trim();
  if (!text) return "—";
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}…`;
};

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function matchesDateRange(value, range) {
  if (!range || range.preset === "All Time" || (!range.startDate && !range.endDate)) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw || raw.toUpperCase() === "N/A") return false;

  const date = utils.parseDateInput(raw);
  if (!date) return false;

  const start = range.startDate ? startOfDay(utils.parseDateInput(range.startDate)) : null;
  const end = range.endDate ? endOfDay(utils.parseDateInput(range.endDate)) : null;

  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
}

export const getStudyGateCode = (row, gateByStudyId = {}) => {
  const studyId = getStudyRowId(row);
  const fromRow = String(
    row?.CurrentGateCode ?? row?.currentGateCode ?? "",
  ).trim();
  if (fromRow) return fromRow;
  return String(gateByStudyId[studyId] || "PRE_STUDY").trim() || "PRE_STUDY";
};

const parseDelimitedSummary = (raw, expectedParts = 2) => {
  const text = String(raw ?? "").trim();
  if (!text) return [];
  return text
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const parts = entry.split("|").map((part) => part.trim());
      return parts;
    })
    .filter((parts) => parts.length >= expectedParts);
};

export const parseAssignedStaffSummary = (raw) => {
  const seen = new Set();
  const users = [];
  for (const parts of parseDelimitedSummary(raw, 2)) {
    const staffId = String(parts[0] || "").trim();
    const name = String(parts[1] || "").trim();
    if (!name) continue;
    const key = staffId || name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    users.push({ staffId: staffId || key, name });
  }
  return users;
};

export const parseMappedSiteSummary = (raw) => {
  const seen = new Set();
  const sites = [];
  for (const parts of parseDelimitedSummary(raw, 2)) {
    const siteId = String(parts[0] || "").trim();
    const name = String(parts[1] || "").trim() || `Site ${siteId}`;
    const abbr = String(parts[2] || "").trim() || name.slice(0, 3).toUpperCase();
    const key = siteId.toLowerCase();
    if (!siteId || seen.has(key)) continue;
    seen.add(key);
    sites.push({ siteId, name, abbr });
  }
  return sites;
};

export const buildStudyEnrichmentMapsFromRows = (rows = []) => {
  const gateByStudyId = {};
  const usersByStudyId = {};
  const sitesByStudyId = {};
  const scheduleStatusByStudyId = {};

  for (const row of rows) {
    const studyId = getStudyRowId(row);
    if (!studyId) continue;

    gateByStudyId[studyId] = getStudyGateCode(row);
    usersByStudyId[studyId] = parseAssignedStaffSummary(
      row?.AssignedStaffSummary ?? row?.assignedStaffSummary,
    );
    sitesByStudyId[studyId] = parseMappedSiteSummary(
      row?.MappedSiteSummary ?? row?.mappedSiteSummary,
    );
    scheduleStatusByStudyId[studyId] = resolveStudyScheduleStatusLabel(row);
  }

  return {
    gateByStudyId,
    usersByStudyId,
    sitesByStudyId,
    scheduleStatusByStudyId,
  };
};

export const getStudyGateLabel = (row, gateByStudyId = {}) =>
  gateNameForCode(getStudyGateCode(row, gateByStudyId));

const sortFilterValues = (values) =>
  [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

export const buildGroupTypeFilterOptions = (rows) => [
  { value: GROUP_TYPE_SINGLE, label: GROUP_TYPE_SINGLE },
  { value: GROUP_TYPE_MULTIPLE, label: GROUP_TYPE_MULTIPLE },
  { value: GROUP_TYPE_NA, label: "Not available" },
].filter((option) =>
  (rows || []).some((row) => normalizeGroupTypeFilterValue(row) === option.value),
);

export const buildPiFilterOptions = (rows) => {
  const set = new Set();
  for (const row of rows || []) {
    const pi = getStudyPiName(row);
    if (pi && pi !== "—") set.add(pi);
  }
  return sortFilterValues([...set]).map((value) => ({ value, label: value }));
};

export const buildStatusFilterOptions = () => [
  { value: "Live", label: "Live" },
  { value: "Archived", label: "Archived" },
];

export const buildGateFilterOptions = () =>
  STUDY_GATE_OPTIONS.map((option) => ({ value: option.value, label: option.label }));

export function applyStudyMgmtFilters(rows, filters, enrichment = {}) {
  if (!filters) return rows || [];
  const gateByStudyId = enrichment.gateByStudyId || {};

  return (rows || []).filter((row) => {
    if (filters.groupTypes?.length) {
      const groupType = normalizeGroupTypeFilterValue(row);
      if (!filters.groupTypes.includes(groupType)) return false;
    }

    if (filters.piNames?.length) {
      const pi = getStudyPiName(row);
      if (!filters.piNames.includes(pi)) return false;
    }

    if (!matchesDateRange(row?.SystemDateTime, filters.createdDateRange)) {
      return false;
    }

    if (filters.status) {
      if (getStudyStatusLabel(row) !== filters.status) return false;
    }

    if (filters.gateCodes?.length) {
      const gateCode = getStudyGateCode(row, gateByStudyId);
      if (!filters.gateCodes.includes(gateCode)) return false;
    }

    return true;
  });
}

const pickFirst = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value != null && value !== "") return value;
  }
  return "";
};

const normalizeUserRows = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const extractStudyUsersFromInfoResponse = (studyRes, staffById = new Map()) => {
  const payload = studyRes?.data ?? studyRes?.Data ?? studyRes;
  const nested = payload?.data ?? payload?.Data ?? payload;
  const rawList = normalizeUserRows(
    pickFirst(nested, ["StudyUsers", "studyUsers", "UserList", "userList", "AssignedUsers", "assignedUsers"]) ||
      pickFirst(payload, ["StudyUsers", "studyUsers", "UserList", "userList"]),
  );

  const users = [];
  const seen = new Set();

  for (const row of rawList) {
    if (!row || typeof row !== "object") continue;
    const staffId = String(
      pickFirst(row, ["UserId", "userId", "StaffId", "staffId", "StaffIDRef", "staffIDRef"]) || "",
    ).trim();
    let name = String(
      pickFirst(row, ["StaffName", "staffName", "UserName", "userName", "Name", "name"]) || "",
    ).trim();

    if (!name && staffId && staffById.has(staffId)) {
      const staff = staffById.get(staffId);
      name =
        staff?.StaffName ||
        `${staff?.FirstName || ""} ${staff?.LastName || ""}`.trim() ||
        "";
    }

    if (!name) {
      name = `${pickFirst(row, ["FirstName", "firstName"]) || ""} ${pickFirst(row, ["LastName", "lastName"]) || ""}`.trim();
    }

    if (!name) continue;
    const key = staffId || name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    users.push({ staffId: staffId || key, name });
  }

  return users;
};

export const extractStudyGateCode = (gateState) =>
  String(gateState?.CurrentGateCode ?? gateState?.GateCode ?? gateState?.currentGateCode ?? "PRE_STUDY").trim() ||
  "PRE_STUDY";

const parseMappedSiteIds = (raw) =>
  String(raw ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const getStudyInfoFromResponse = (studyRes) => {
  const payload = studyRes?.data ?? studyRes?.Data ?? studyRes;
  const nested = payload?.data ?? payload?.Data ?? payload;

  if (
    pickFirst(nested, ["StudyNumber", "StudyId", "StudyName", "MappedSiteIds"]) ||
    pickFirst(nested, ["studyNumber", "studyId", "studyName", "mappedSiteIds"])
  ) {
    return nested;
  }

  if (
    pickFirst(payload, ["StudyNumber", "StudyId", "StudyName", "MappedSiteIds"]) ||
    pickFirst(payload, ["studyNumber", "studyId", "studyName", "mappedSiteIds"])
  ) {
    return payload;
  }

  const table = payload?.Table ?? payload?.table;
  if (Array.isArray(table) && table[0] && typeof table[0] === "object") {
    return table[0];
  }

  const studyInfo = nested?.StudyInfo ?? nested?.studyInfo ?? payload?.StudyInfo;
  if (Array.isArray(studyInfo) && studyInfo[0]) return studyInfo[0];
  if (studyInfo && typeof studyInfo === "object") return studyInfo;
  return null;
};

const buildSiteLookupMap = (siteRows = []) => {
  const siteById = new Map();
  for (const site of siteRows) {
    const id = String(site?.SiteId ?? site?.SiteID ?? site?.siteId ?? "").trim();
    if (!id) continue;
    siteById.set(id, site);
    siteById.set(id.toLowerCase(), site);
  }
  return siteById;
};

export const extractStudySitesFromInfoResponse = (studyRes, siteRows = []) => {
  const info = getStudyInfoFromResponse(studyRes);
  if (!info) return [];

  const siteById = buildSiteLookupMap(siteRows);
  let mappedSiteIds = parseMappedSiteIds(
    pickFirst(info, ["MappedSiteIds", "mappedSiteIds"]),
  );

  if (!mappedSiteIds.length) {
    const singleSiteId = pickFirst(info, [
      "SiteID",
      "SiteId",
      "siteId",
      "siteID",
      "StudySiteID",
      "studySiteID",
    ]);
    if (singleSiteId) mappedSiteIds = [String(singleSiteId)];
  }

  const sites = [];
  const seen = new Set();

  for (const siteId of mappedSiteIds) {
    const normalizedId = String(siteId).trim();
    const lookupKey = normalizedId.toLowerCase();
    if (!normalizedId || seen.has(lookupKey)) continue;
    seen.add(lookupKey);

    const site =
      siteById.get(normalizedId) ||
      siteById.get(lookupKey);
    const name = String(site?.SiteName ?? site?.siteName ?? "").trim() || `Site ${normalizedId}`;
    const abbr = String(site?.SiteAbbr ?? site?.siteAbbr ?? "").trim() || name.slice(0, 3).toUpperCase();

    sites.push({
      siteId: normalizedId,
      name,
      abbr,
    });
  }

  return sites;
};
